import { Shell } from "@/components/layout/Shell";
import { useVeiculos, useParceirosParaVeiculos } from "@/hooks/use-data";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Car,
  Plus,
  X,
  Loader2,
  Lock,
  Pencil,
  Trash2,
  Hash,
  Calendar,
  Upload,
  Search,
} from "lucide-react";
import { useState, useMemo, useRef } from "react";
import { ImportarVeiculosDialog } from "@/components/veiculos/ImportarVeiculosDialog";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import type { VeiculoComLaudo as Veiculo, VeiculoFoto } from "@shared/schema";
import { POSICOES_VEICULO, ROTULO_POSICAO_VEICULO, type PosicaoVeiculo } from "@shared/veiculo-fotos";
import { formatPlaca } from "@/lib/formatters";
import {
  VEICULO_TIPO_LABELS,
  VEICULO_TIPO_COLORS,
  MARCAS_POR_TIPO,
  CORES,
} from "@/lib/constants";
import { PreencherTesteButton } from "@/components/PreencherTesteButton";
import {
  gerarPlacaFake,
  gerarChassiFake,
  gerarRenavamFake,
} from "@/lib/mock-test-data";
import { useIsEmpresaTestMode } from "@/hooks/use-modo-teste";
import { useModulos } from "@/hooks/use-modulos";
import { FotosVeiculo, FotosVeiculoRascunho, chaveFotosVeiculo, type ArquivosFotosVeiculo } from "@/components/veiculos/FotosVeiculo";

const currentYear = new Date().getFullYear();

function digitosFake(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

type VeiculoFormData = {
  tipo: string;
  placa: string;
  numeroNota: string;
  chassi: string;
  cor: string;
  marca: string;
  modelo: string;
  anoFabricacao: string;
  anoModelo: string;
  numeroMotor: string;
  renavam: string;
};

const EMPTY_FORM: VeiculoFormData = {
  tipo: "",
  placa: "",
  numeroNota: "",
  chassi: "",
  cor: "",
  marca: "",
  modelo: "",
  anoFabricacao: "",
  anoModelo: "",
  numeroMotor: "",
  renavam: "",
};

export function VeiculoForm({
  initial,
  parceiroId,
  onClose,
  parceiros,
  isVistoriadora,
  parceirosLoading,
}: {
  initial?: Veiculo;
  parceiroId: string;
  onClose: () => void;
  parceiros?: any[];
  isVistoriadora?: boolean;
  parceirosLoading?: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const { modulos } = useModulos();
  const isEdit = !!initial;
  // Com o módulo ligado, o veículo recém-cadastrado fica na tela para receber as fotos; "Concluir" fecha.
  const [criado, setCriado] = useState<Veiculo | null>(null);
  const criadoRef = useRef<Veiculo | null>(null);
  const enviandoRef = useRef(false);
  const concluirAposSalvarRef = useRef(false);
  const [fotosPendentes, setFotosPendentes] = useState<ArquivosFotosVeiculo>({});
  const [fotosSalvas, setFotosSalvas] = useState<Partial<Record<PosicaoVeiculo, VeiculoFoto>>>({});
  const [fotoEditando, setFotoEditando] = useState(false);
  const [confirmarFechamento, setConfirmarFechamento] = useState(false);
  const temFotosPendentes = Object.keys(fotosPendentes).length > 0;
  const veiculoId = initial?.id ?? criado?.id;
  // T23: com qualquer laudo vinculado o cadastro é somente leitura (o banco também recusa). As fotos seguem editáveis.
  const somenteLeitura = !!initial?.temLaudo;

  const [form, setForm] = useState<VeiculoFormData>(
    initial
      ? {
          tipo: initial.tipo,
          placa: initial.placa,
          numeroNota: initial.numeroNota,
          chassi: initial.chassi,
          cor: initial.cor,
          marca: initial.marca,
          modelo: initial.modelo,
          anoFabricacao: initial.anoFabricacao
            ? String(initial.anoFabricacao)
            : "",
          anoModelo: initial.anoModelo ? String(initial.anoModelo) : "",
          numeroMotor: initial.numeroMotor ?? "",
          renavam: initial.renavam ?? "",
        }
      : EMPTY_FORM,
  );

  const [selectedParceiroId, setSelectedParceiroId] = useState(parceiroId);
  // Depois de "Cadastrar" o formulário continua editável; este é o último estado gravado, para o "Concluir" saber se há edição pendente.
  const [gravado, setGravado] = useState<string | null>(null);
  const formAtual = JSON.stringify([form, selectedParceiroId]);
  const fechar = () => {
    if (enviandoRef.current || fotoEditando) return;
    if (temFotosPendentes) setConfirmarFechamento(true);
    else onClose();
  };

  const isTestMode = useIsEmpresaTestMode(selectedParceiroId || parceiroId);

  const preencherTeste = () => {
    setForm((f) => ({
      ...f,
      tipo: f.tipo || "auto_leve",
      placa: gerarPlacaFake(),
      numeroNota: f.numeroNota || digitosFake(),
      chassi: gerarChassiFake(),
      cor: f.cor || `${CORES[0].codigo} - ${CORES[0].nome}`,
      marca: f.marca || (MARCAS_POR_TIPO["auto_leve"]?.[0] ?? ""),
      modelo: f.modelo || "Modelo Teste",
      anoFabricacao: f.anoFabricacao || String(currentYear),
      anoModelo: f.anoModelo || String(currentYear),
      renavam: gerarRenavamFake(),
    }));
  };

  const set =
    (field: keyof VeiculoFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleTipoChange = (v: string) => {
    setForm((f) => ({
      ...f,
      tipo: v,
      // reset marca se não existe no novo tipo
      marca: MARCAS_POR_TIPO[v]?.includes(f.marca) ? f.marca : "",
    }));
  };

  const handleYearChange = (field: "fab" | "model", value: string) => {
    // Remove tudo que não for número
    const numeric = value.replace(/\D/g, "");

    // Limita a 4 caracteres
    const limited = numeric.slice(0, 4);

    // Atualiza estado
    setForm((prev) => ({
      ...prev,
      anoFabricacao: field === "fab" ? limited : prev.anoFabricacao,
      anoModelo: field === "model" ? limited : prev.anoModelo,
    }));
  };

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const idAlvo = initial?.id ?? criadoRef.current?.id;
      let veiculoSalvo = initial ?? criadoRef.current;
      if (!idAlvo || isEdit || formAtual !== gravado) {
      const url = idAlvo ? `/api/veiculos/${idAlvo}` : "/api/veiculos";
      const method = idAlvo ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erro ao salvar veículo");
      }
      veiculoSalvo = await res.json();
      if (!veiculoSalvo?.id) throw new Error("Não foi possível confirmar o cadastro do veículo.");
      if (!isEdit) {
        criadoRef.current = veiculoSalvo;
        setCriado(veiculoSalvo);
      }
      setGravado(formAtual);
      }
      if (!veiculoSalvo?.id) throw new Error("Não foi possível confirmar o cadastro do veículo.");
      for (const posicao of POSICOES_VEICULO) {
        const file = fotosPendentes[posicao];
        if (!file) continue;
        const fotoRes = await fetch(`/api/veiculos/${veiculoSalvo.id}/fotos/${posicao}`, {
          method: "POST", headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": file.type }, body: file,
        }).catch(() => null);
        const foto = await fotoRes?.json().catch(() => null);
        if (!fotoRes?.ok || !foto?.fotoUrl) {
          throw new Error(`O cadastro foi salvo, mas a foto ${ROTULO_POSICAO_VEICULO[posicao].toLowerCase()} não foi enviada. Toque em Concluir para tentar novamente.`);
        }
        setFotosSalvas((atuais) => ({ ...atuais, [posicao]: foto }));
        setFotosPendentes((atuais) => { const novas = { ...atuais }; if (novas[posicao] === file) delete novas[posicao]; return novas; });
      }
      queryClient.invalidateQueries({ queryKey: chaveFotosVeiculo(veiculoSalvo.id) });
      return veiculoSalvo;
    },
    onSuccess: (data: Veiculo) => {
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      toast({
        title: isEdit ? "Veículo atualizado" : "Veículo cadastrado",
        description: isEdit
          ? "As informações foram salvas com sucesso."
          : "O veículo foi cadastrado com sucesso.",
      });
      if (!isEdit && modulos.detranVeiculoFoto && !concluirAposSalvarRef.current) return;
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
    onSettled: () => { enviandoRef.current = false; },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (somenteLeitura || enviandoRef.current || fotoEditando) return;
    // "Concluir" depois de cadastrar: sem edição pendente só fecha; com edição, grava antes (nunca descarta em silêncio).
    if (criado && formAtual === gravado && !temFotosPendentes) {
      onClose();
      return;
    }
    if (
      !form.tipo ||
      !form.placa ||
      !form.numeroNota ||
      !form.chassi ||
      !form.cor ||
      !form.marca ||
      !form.modelo
    )
      return;

    if (isVistoriadora && !selectedParceiroId) {
      toast({
        title: "Validação",
        description: "Selecione um parceiro de negócio",
        variant: "destructive",
      });
      return;
    }

    const year = Number(form.anoFabricacao);

    if (year < 1900 || year > currentYear + 1) {
      toast({
        title: "Validação",
        description: "Ano de fabricação inválido",
        variant: "destructive",
      });
      return;
    }

    if (year < 1900 || year > currentYear + 2) {
      toast({
        title: "Validação",
        description: "Ano de modelo inválido",
        variant: "destructive",
      });
      return;
    }

    enviandoRef.current = true;
    concluirAposSalvarRef.current = !!criadoRef.current;
    mutation.mutate({
      parceiroId: selectedParceiroId,
      tipo: form.tipo,
      placa: form.placa.toUpperCase().replace(/\s/g, ""),
      numeroNota: form.numeroNota,
      chassi: form.chassi.toUpperCase(),
      cor: form.cor,
      marca: form.marca,
      modelo: form.modelo,
      anoFabricacao: form.anoFabricacao
        ? parseInt(form.anoFabricacao)
        : undefined,
      anoModelo: form.anoModelo ? parseInt(form.anoModelo) : undefined,
      numeroMotor: form.numeroMotor || undefined,
      renavam: form.renavam || undefined,
    });
  };

  return (
    <Card className="border-2 border-primary/20 shadow-lg">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-4">
        <CardTitle className="text-base">
          {isEdit ? "Editar Veículo" : "Novo Veículo"}
        </CardTitle>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {isTestMode && <PreencherTesteButton onFill={preencherTeste} />}
          <Button variant="ghost" size="icon" disabled={mutation.isPending || fotoEditando} onClick={fechar} aria-label="Fechar cadastro do veículo">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {somenteLeitura && (
            <div role="status" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                Este veículo tem laudo vinculado e não pode mais ser editado ou excluído. As fotos continuam podendo ser enviadas.
              </p>
            </div>
          )}
          {/* Só o cadastro trava; as fotos (abaixo) ficam fora do fieldset porque seguem editáveis. */}
          <fieldset disabled={somenteLeitura || mutation.isPending} className="m-0 min-w-0 space-y-4 border-0 p-0">
          {isVistoriadora && (
            <div className="space-y-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <Label>Parceiro de Negócio *</Label>
              {parceirosLoading ? (
                <div className="text-sm text-muted-foreground py-2">
                  Carregando parceiros...
                </div>
              ) : !parceiros || parceiros.length === 0 ? (
                <div className="text-sm text-red-600 py-2">
                  Nenhum parceiro de negócio encontrado. Certifique-se de que há
                  parceiros vinculados a essa vistoriadora.
                </div>
              ) : (
                <Select
                  value={selectedParceiroId}
                  onValueChange={setSelectedParceiroId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o parceiro" />
                  </SelectTrigger>
                  <SelectContent>
                    {parceiros.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nomeFantasia || p.razaoSocial}
                        {p.idProprio != null ? ` (${p.idProprio})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                O veículo será cadastrado para este parceiro de negócio
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Veículo *</Label>
              <Select value={form.tipo} onValueChange={handleTipoChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VEICULO_TIPO_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Placa *</Label>
              <Input
                value={formatPlaca(form.placa)}
                onChange={set("placa")}
                placeholder="ABC1D23"
                required
                maxLength={8}
                className="uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label>Nº da Nota *</Label>
              <Input
                value={form.numeroNota}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    numeroNota: e.target.value.replace(/\D/g, "").slice(0, 9),
                  }))
                }
                placeholder="000000000"
                inputMode="numeric"
                maxLength={9}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Chassi *</Label>
              <Input
                value={form.chassi}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    chassi: e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .toUpperCase()
                      .slice(0, 17),
                  }))
                }
                placeholder="17 caracteres alfanuméricos"
                required
                maxLength={17}
                className="uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label>Cor *</Label>
              <Select
                value={form.cor}
                onValueChange={(v) => setForm((f) => ({ ...f, cor: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a cor" />
                </SelectTrigger>
                <SelectContent>
                  {CORES.map((c) => (
                    <SelectItem
                      key={c.codigo}
                      value={`${c.codigo} - ${c.nome}`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0"
                          style={{
                            background:
                              c.hex ??
                              "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)",
                          }}
                        />
                        {c.codigo} - {c.nome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Marca *</Label>
              <Select
                value={form.marca}
                onValueChange={(v) => setForm((f) => ({ ...f, marca: v }))}
                disabled={!form.tipo}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      form.tipo
                        ? "Selecione a marca"
                        : "Selecione o tipo primeiro"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(MARCAS_POR_TIPO[form.tipo] ?? []).map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Modelo *</Label>
              <Input value={form.modelo} onChange={set("modelo")} required />
            </div>

            <div className="space-y-2">
              <Label>Ano Fabricação</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.anoFabricacao}
                onChange={(e) => handleYearChange("fab", e.target.value)}
                placeholder="ex: 2022"
                maxLength={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Ano Modelo</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.anoModelo}
                onChange={(e) => handleYearChange("model", e.target.value)}
                placeholder="ex: 2023"
                maxLength={4}
              />
            </div>

            <div className="space-y-2">
              <Label>Nº do Motor</Label>
              <Input
                value={form.numeroMotor}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    numeroMotor: e.target.value
                      .replace(/[^A-Za-z0-9]/g, "")
                      .toUpperCase(),
                  }))
                }
                className="uppercase"
                placeholder="Alfanumérico"
              />
            </div>

            <div className="space-y-2">
              <Label>RENAVAM</Label>
              <Input
                value={form.renavam}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    renavam: e.target.value.replace(/\D/g, "").slice(0, 11),
                  }))
                }
                placeholder="9 a 11 dígitos"
                inputMode="numeric"
                maxLength={11}
              />
            </div>
          </div>
          </fieldset>

          {modulos.detranVeiculoFoto && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Fotos do veículo</h3>
              <p className="text-xs text-muted-foreground">
                Frente, traseira e as duas laterais. Necessárias para atribuir o técnico de vistoria.
              </p>
              {isEdit && (
                <p className="text-xs text-muted-foreground" aria-live="polite">
                  {fotoEditando ? "Enviando foto…" : "Cada foto é salva automaticamente ao ser enviada."}
                </p>
              )}
              {isEdit && veiculoId ? (
                <FotosVeiculo veiculoId={veiculoId} onEnviandoChange={setFotoEditando} />
              ) : (
                <FotosVeiculoRascunho arquivos={fotosPendentes} salvas={fotosSalvas} desabilitado={mutation.isPending}
                  onArquivo={(posicao, file) => setFotosPendentes((atuais) => {
                    const novas = { ...atuais };
                    if (file) novas[posicao] = file; else delete novas[posicao];
                    return novas;
                  })} />
              )}
            </section>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {somenteLeitura ? (
              <Button type="button" disabled={fotoEditando} onClick={fechar}>
                Concluir
              </Button>
            ) : criado ? (
              <Button type="submit" disabled={mutation.isPending || fotoEditando}>
                {mutation.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                Concluir
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" disabled={mutation.isPending || fotoEditando} onClick={fechar}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={mutation.isPending || fotoEditando}>
                  {mutation.isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {isEdit ? "Salvar" : "Cadastrar"}
                </Button>
              </>
            )}
          </div>
        </form>
        <AlertDialog open={confirmarFechamento} onOpenChange={setConfirmarFechamento}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Descartar fotos não enviadas?</AlertDialogTitle>
              <AlertDialogDescription>Há fotos selecionadas que ainda não foram salvas. As fotos já enviadas serão mantidas.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Continuar no cadastro</AlertDialogCancel>
              <AlertDialogAction onClick={onClose}>Descartar e fechar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

function VeiculoCard({
  veiculo,
  canEdit,
}: {
  veiculo: Veiculo;
  canEdit: boolean;
}) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/veiculos/${veiculo.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erro ao remover veículo");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      toast({
        title: "Veículo removido",
        description: `${veiculo.marca} ${veiculo.modelo} foi removido.`,
      });
    },
    onError: (err: Error) => {
      // Se o veículo ganhou laudo desde a última listagem, recarrega para o cartão perder o "Excluir".
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      toast({
        title: "Erro",
        description: err.message || "Não foi possível remover o veículo.",
        variant: "destructive",
      });
    },
  });

  if (editing) {
    return (
      <VeiculoForm
        initial={veiculo}
        parceiroId={veiculo.parceiroId}
        onClose={() => setEditing(false)}
      />
    );
  }

  return (
    <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
              <Car className="w-5 h-5 text-slate-500" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base">
                {veiculo.marca} {veiculo.modelo}
              </CardTitle>
              <p className="text-xs text-muted-foreground font-mono mt-0.5">
                {veiculo.placa}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Badge
              className={
                VEICULO_TIPO_COLORS[veiculo.tipo] ??
                "bg-slate-100 text-slate-700"
              }
            >
              {VEICULO_TIPO_LABELS[veiculo.tipo] ?? veiculo.tipo}
            </Badge>
            {canEdit && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={veiculo.temLaudo ? "Ver veículo (somente leitura)" : "Editar veículo"}
                  title={veiculo.temLaudo ? "Ver veículo (somente leitura)" : "Editar veículo"}
                  onClick={() => setEditing(true)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                {!veiculo.temLaudo && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover veículo?</AlertDialogTitle>
                      <AlertDialogDescription>
                        O veículo{" "}
                        <strong>
                          {veiculo.marca} {veiculo.modelo} — {veiculo.placa}
                        </strong>{" "}
                        será removido.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteMutation.mutate()}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Remover
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                )}
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-600">
          <div className="flex items-center gap-1.5">
            <Hash className="w-3.5 h-3.5 text-slate-400" />
            <span className="truncate">Nota: {veiculo.numeroNota}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 text-xs">Chassi:</span>
            <span className="font-mono text-xs">{veiculo.chassi}</span>
          </div>
          {(veiculo.anoFabricacao || veiculo.anoModelo) && (
            <div className="flex items-center gap-1.5 col-span-2">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <span>
                {veiculo.anoFabricacao && `Fab. ${veiculo.anoFabricacao}`}
                {veiculo.anoFabricacao && veiculo.anoModelo && " / "}
                {veiculo.anoModelo && `Mod. ${veiculo.anoModelo}`}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400 text-xs">Cor:</span>
            {(() => {
              const c = CORES.find(
                (x) =>
                  x.nome === veiculo.cor ||
                  `${x.codigo} - ${x.nome}` === veiculo.cor,
              );
              return (
                <div className="flex items-center gap-1.5">
                  {c && (
                    <span
                      className="w-3 h-3 rounded-full border border-slate-300 shrink-0"
                      style={{
                        background:
                          c.hex ??
                          "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)",
                      }}
                    />
                  )}
                  <span>{veiculo.cor}</span>
                </div>
              );
            })()}
          </div>
          {veiculo.renavam && (
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-xs">RENAVAM:</span>
              <span className="font-mono text-xs">{veiculo.renavam}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Veiculos() {
  const { profile } = useAuth();
  const roles = profile?.roles || [];
  const isParceiro = roles.includes("parceiro_negocios");
  const isOwner = roles.includes("owner");
  const isAdmin = roles.includes("admin");
  const isVistoriadora =
    roles.includes("admin") || roles.includes("rt") || roles.includes("tv");

  // Parceiros só veem os próprios veículos; vistoriadora/owner podem filtrar por parceiroId
  const parceiroIdFiltro = isParceiro ? profile?.empresaId : undefined;
  const { data: veiculos, isLoading } = useVeiculos(parceiroIdFiltro);

  // Vistoriadora precisa dos parceiros para agrupar a listagem
  const { data: parceiros, isLoading: parceirosLoading } =
    useParceirosParaVeiculos(isVistoriadora);

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");
  const [filterParceiroId, setFilterParceiroId] = useState("all");

  // Parceiros cadastram sempre na própria empresa; vistoriadora escolhe o parceiro
  const parceiroIdForm = profile?.empresaId ?? "";

  const veiculosFiltrados = useMemo(() => {
    if (!veiculos) return undefined;
    let list = veiculos;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (v) =>
          v.placa.toLowerCase().includes(q) ||
          v.marca.toLowerCase().includes(q) ||
          v.modelo.toLowerCase().includes(q) ||
          v.chassi.toLowerCase().includes(q),
      );
    }
    if (filterTipo !== "all") {
      list = list.filter((v) => v.tipo === filterTipo);
    }
    if (filterParceiroId !== "all") {
      list = list.filter((v) => v.parceiroId === filterParceiroId);
    }
    return list;
  }, [veiculos, search, filterTipo, filterParceiroId]);

  // Apenas admins da vistoriadora ou parceiros podem criar veículos
  const canCreate = isParceiro || isOwner || isAdmin;

  return (
    <Shell>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-slate-900">
            Veículos
          </h1>
          <p className="text-muted-foreground mt-1">
            {isParceiro
              ? "Gerencie os veículos cadastrados na sua empresa"
              : isVistoriadora && isAdmin
                ? "Cadastre veículos para os seus parceiros de negócio"
                : "Veículos cadastrados pelos parceiros"}
          </p>
        </div>
        {canCreate && !showForm && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Importar em Lote
            </Button>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Veículo
            </Button>
          </div>
        )}
      </div>

      {showForm && (
        <div className="mb-6">
          <VeiculoForm
            parceiroId={parceiroIdForm}
            onClose={() => setShowForm(false)}
            parceiros={parceiros}
            isVistoriadora={isVistoriadora && isAdmin}
            parceirosLoading={parceirosLoading}
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por placa, marca, modelo ou chassi..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-[150px] h-9">
            <SelectValue placeholder="Tipo de veículo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {Object.entries(VEICULO_TIPO_LABELS).map(([val, label]) => (
              <SelectItem key={val} value={val}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isVistoriadora && parceiros && parceiros.length > 0 && (
          <Select value={filterParceiroId} onValueChange={setFilterParceiroId}>
            <SelectTrigger className="w-[200px] h-9">
              <SelectValue placeholder="Todos os parceiros" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os parceiros</SelectItem>
              {parceiros.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.nomeFantasia || p.razaoSocial}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {(search || filterTipo !== "all" || filterParceiroId !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch("");
              setFilterTipo("all");
              setFilterParceiroId("all");
            }}
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-44 rounded-xl" />
            ))}
        </div>
      ) : !veiculosFiltrados || veiculosFiltrados.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Car className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium">Nenhum veículo cadastrado</p>
          {canCreate && (
            <p className="text-sm mt-1">
              Clique em <strong>Novo Veículo</strong> para começar.
            </p>
          )}
        </div>
      ) : isVistoriadora ? (
        // Vistoriadora: agrupa por empresa parceiro
        (() => {
          const grupos = (veiculosFiltrados ?? []).reduce<Record<string, Veiculo[]>>(
            (acc, v) => {
              const pid = v.parceiroId;
              if (!acc[pid]) acc[pid] = [];
              acc[pid].push(v);
              return acc;
            },
            {},
          );

          return (
            <div className="space-y-8">
              {Object.entries(grupos).map(([parceiroId, pvs]) => {
                const emp = parceiros?.find((p) => p.id === parceiroId);
                const nome =
                  emp?.nomeFantasia ||
                  emp?.razaoSocial ||
                  parceiroId.slice(0, 8).toUpperCase();
                return (
                  <div key={parceiroId}>
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className="text-base font-semibold text-slate-800">
                        {nome}
                      </h2>
                      <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded-full">
                        {pvs.length} veículo{pvs.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {pvs.map((v) => (
                        <VeiculoCard key={v.id} veiculo={v} canEdit={isAdmin} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
          {(veiculosFiltrados ?? []).map((v) => (
            <VeiculoCard
              key={v.id}
              veiculo={v}
              canEdit={isParceiro || isOwner}
            />
          ))}
        </div>
      )}

      <ImportarVeiculosDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        isVistoriadoraAdmin={isVistoriadora && isAdmin}
      />
    </Shell>
  );
}
